import { z } from 'zod';

import { axiosRubyClient } from '@/util/axios/axiosClient';

// Surge pay (GRAVY-10443) — REST client and response contract.
//
// Every call parses its response, so a payload that drifts from graphy's
// PayEscalation::Builder fails loudly here instead of rendering a banner from
// half a schedule. Money is cents throughout, as graphy stores it.

const limits = {
  max_cents: z.number().int(),
  lead_time_options_minutes: z.array(z.number().int()),
};

const clientTier = z.object({
  lead_time_minutes: z.number().int(),
  rate_cents: z.number().int(),
});

export const clientSurgeSchema = z.object({
  customer_id: z.number().int(),
  enabled: z.boolean(),
  tiers: z.array(clientTier),
  updated_at: z.string().nullish(),
  ...limits,
});

const jobTier = clientTier.extend({
  fires_at: z.string().nullish(),
  state: z.enum(['pending', 'fired', 'skipped']),
  fired_at: z.string().nullish(),
});

export const jobSurgeSchema = z.object({
  job_id: z.number().int(),
  status: z.enum(['off', 'upcoming', 'active', 'paused', 'ended']),
  enabled: z.boolean(),
  paused_at: z.string().nullish(),
  ended_at: z.string().nullish(),
  applied_cents: z.number().int(),
  base_pay_rate: z.number().int(),
  pay_rate: z.number().int(),
  first_shift_start_at: z.string().nullish(),
  /** How many shifts a raise on this job would reach (Decision 2, 2026-09-30). */
  shifts_count: z.number().int().default(1),
  /**
   * Whether the automation can actually run for this job (GRAVY-10869). False
   * on a private/unposted job: the schedule is shown, but the sweep only
   * snapshots posted jobs, so a countdown there would never come true.
   */
  will_apply: z.boolean().default(true),
  /**
   * Whether the platform surge automation (the `pay-escalation` kill switch)
   * is on. False means no sweep runs anywhere, so nothing will fire on its own
   * — manual override still works. Defaults to true for older backends that
   * do not send it.
   */
  automation_on: z.boolean().default(true),
  next_tier: jobTier.nullish(),
  /**
   * The tier the next sweep will apply: its moment has passed but the sweep
   * has not run since (GRAVY-10871). `next_tier` only looks forward, so this is
   * what keeps the banner — and its controls — up in that gap.
   */
  due_tier: jobTier.nullish(),
  tiers: z.array(jobTier),
  ...limits,
});

const envelope = <T extends z.ZodTypeAny>(data: T) =>
  z.object({ success: z.literal(true), data });

export type SurgeTier = z.infer<typeof clientTier>;
export type ClientSurge = z.infer<typeof clientSurgeSchema>;
export type JobSurge = z.infer<typeof jobSurgeSchema>;
export type JobSurgeTier = z.infer<typeof jobTier>;

export type SurgeSchedule = { enabled: boolean; tiers: SurgeTier[] };

const clientPath = (customerId: number | string) =>
  `/api/admin/customers/${customerId}/pay_escalation`;
const jobPath = (jobId: number | string, action?: string) =>
  `/api/admin/jobs/${jobId}/pay_escalation${action ? `/${action}` : ''}`;

const parseClient = (data: unknown) =>
  envelope(clientSurgeSchema).parse(data).data;
const parseJob = (data: unknown) => envelope(jobSurgeSchema).parse(data).data;

export const paySurgeClient = {
  async getClientSurge(customerId: number | string): Promise<ClientSurge> {
    const { data } = await axiosRubyClient.get(clientPath(customerId));
    return parseClient(data);
  },

  async saveClientSurge(
    customerId: number | string,
    schedule: SurgeSchedule,
  ): Promise<ClientSurge> {
    const { data } = await axiosRubyClient.put(
      clientPath(customerId),
      schedule,
    );
    return parseClient(data);
  },

  async getJobSurge(jobId: number | string): Promise<JobSurge> {
    const { data } = await axiosRubyClient.get(jobPath(jobId));
    return parseJob(data);
  },

  async saveJobSurge(
    jobId: number | string,
    schedule: SurgeSchedule,
  ): Promise<JobSurge> {
    const { data } = await axiosRubyClient.put(jobPath(jobId), schedule);
    return parseJob(data);
  },

  async pauseJobSurge(jobId: number | string): Promise<JobSurge> {
    const { data } = await axiosRubyClient.post(jobPath(jobId, 'pause'), {});
    return parseJob(data);
  },

  async resumeJobSurge(jobId: number | string): Promise<JobSurge> {
    const { data } = await axiosRubyClient.post(jobPath(jobId, 'resume'), {});
    return parseJob(data);
  },

  async overrideJobSurge(
    jobId: number | string,
    amountCents: number,
  ): Promise<JobSurge> {
    const { data } = await axiosRubyClient.post(jobPath(jobId, 'override'), {
      amount_cents: amountCents,
    });
    return parseJob(data);
  },
};

/**
 * The message the API sent (`errors[0]`), or null — the same reading as
 * Dashboard V2's `apiErrorMessage`, so a refusal such as "Surge pay can only be
 * raised" reaches the admin in the server's words.
 */
export const surgeErrorMessage = (e: unknown): string | null => {
  const body = (e as { response?: { data?: { errors?: unknown } } })?.response
    ?.data;
  const first = Array.isArray(body?.errors) ? body?.errors[0] : undefined;
  return typeof first === 'string' && first.trim() ? first : null;
};
